/**
 * The **draggable in-world window** primitive (Chassis Slice H, PRD #534 D7 /
 * ADR-0271): the shared chassis under the leaderboard Top Banksitters window
 * (ADR-0045) and the Arena Master gump (ADR-0245), which shipped as copies of
 * each other. A future gump composes this instead of copying either.
 *
 * What the primitive owns (the part the twins duplicated):
 *
 * - **The window chrome DOM** (component-owned, so `index.html` stops accreting
 *   per-window blocks): the `#<id>` root (hidden until opened), the `#<id>-bar`
 *   title bar with its `<h2>` and the `#<id>-close` ✕ button - the same element
 *   ids the declared CSS binds to. Pre-existing markup with the root id is
 *   adopted as-is (a fixture-mounted test document keeps working); otherwise the
 *   chrome is built and appended to `document.body`, and the consumer appends
 *   its content into the root after the bar.
 * - **First-open centring**: the window lands centred (a third down) on first
 *   open; later opens keep wherever it was dragged.
 * - **Drag by the title bar**: delta-based (start pointer + start left/top) so it
 *   needs no getBoundingClientRect, with the move/up listeners on the document so
 *   a drag continues even when the pointer outruns the bar. Pointer events
 *   coexist with the canvas right-click movement (the overlay is a sibling of the
 *   canvas, so a drag here never reaches the movement handlers).
 * - **The close gestures**: a right-click anywhere on the window (the classic UO
 *   gump gesture; the browser context menu is suppressed so the click only
 *   closes), the ✕ in the bar (the discoverable left-click/keyboard equivalent),
 *   and - opt-in per window - Escape while open.
 *
 * Consumers own their content and their open/close side-effects (fetch + poll
 * lifecycles) via the `onOpen`/`onClose` hooks; the primitive holds no state
 * beyond `opened`.
 */

/** What a window instance declares. */
export interface UiWindowSpec {
  /** The document to wire against. */
  doc: Document;
  /** The root element id (`#board-window` / `#arena-gump`) - the CSS binds to it. */
  id: string;
  /** The `<h2>` title in the drag bar. */
  title: string;
  /** The root's `aria-label`. */
  ariaLabel: string;
  /** Fallback size for first-open centring before layout (offsetWidth is 0 in jsdom). */
  fallbackWidth: number;
  fallbackHeight: number;
  /** Place the first open at the exact viewport centre instead of the usual one-third-down gump position. */
  centerVertically?: boolean;
  /** Close on Escape while open (the arena gump); off by default (the leaderboard window). */
  escapeCloses?: boolean;
  /**
   * Keep the window inside the viewport (PRD #629 story 18, the paperdoll): the
   * placement on open and every drag step are clamped so the window can never
   * strand off-screen. Off by default (the pre-existing windows keep their
   * unclamped drag).
   */
  clampToViewport?: boolean;
  /**
   * Re-append the root to its parent on every open, so an already-open window
   * called again paints above later-added same-z siblings (the paperdoll's
   * raise-on-reopen). Off by default.
   */
  raiseOnOpen?: boolean;
  /** Consumer side-effects: start/stop the fetch + poll lifecycle. */
  onOpen?: () => void;
  onClose?: () => void;
}

/** The handle a consumer builds its window module around. */
export interface UiWindow {
  /** The root element (append content after the bar). */
  root: HTMLElement;
  /** Show the window (centring it on first open) and run the consumer's onOpen. */
  open(): void;
  /** Hide the window and run the consumer's onClose. */
  close(): void;
  /** Whether the window is currently shown. */
  isOpen(): boolean;
}

/** Adopt existing `#<id>` chrome or build + append the component's own. */
function ensureChrome(spec: UiWindowSpec): HTMLElement | null {
  const { doc } = spec;
  const existing = doc.getElementById(spec.id);
  if (existing) {
    existing.classList.add('ui-window');
    return existing;
  }
  if (!doc.body) return null;
  const root = doc.createElement('div');
  root.id = spec.id;
  root.classList.add('ui-window');
  root.setAttribute('aria-label', spec.ariaLabel);
  root.hidden = true;
  const bar = doc.createElement('div');
  bar.id = `${spec.id}-bar`;
  const h2 = doc.createElement('h2');
  h2.textContent = spec.title;
  const closeBtn = doc.createElement('button');
  closeBtn.id = `${spec.id}-close`;
  closeBtn.type = 'button';
  closeBtn.setAttribute('aria-label', 'Close');
  closeBtn.title = 'Close (or right-click)';
  closeBtn.textContent = '✕';
  bar.append(h2, closeBtn);
  root.appendChild(bar);
  doc.body.appendChild(root);
  return root;
}

/**
 * Build (or adopt) a window's chrome and wire the shared open/close/drag/close
 * gestures. Returns `null` only for a body-less document (the caller degrades to
 * a no-op handle - the resilience rule every window module already follows).
 */
export function createUiWindow(spec: UiWindowSpec): UiWindow | null {
  const { doc } = spec;
  const root = ensureChrome(spec);
  if (!root) return null;
  const bar = doc.getElementById(`${spec.id}-bar`);
  const closeBtn = doc.getElementById(`${spec.id}-close`);
  if (!bar) return null;

  let opened = false;

  /** Centre the window the first time it opens; later opens keep where it was dragged. */
  const placeIfUnplaced = (): void => {
    if (root.style.left) return;
    const view = doc.defaultView;
    const vw = view?.innerWidth ?? 800;
    const vh = view?.innerHeight ?? 600;
    const w = root.offsetWidth || spec.fallbackWidth;
    const h = root.offsetHeight || spec.fallbackHeight;
    root.style.left = `${Math.max(8, (vw - w) / 2)}px`;
    root.style.top = `${Math.max(8, (vh - h) / (spec.centerVertically ? 2 : 3))}px`;
  };

  /** Clamp the window's placement into the viewport (opt-in, story 18). */
  const clamp = (): void => {
    if (!spec.clampToViewport) return;
    const view = doc.defaultView;
    const vw = view?.innerWidth ?? 800;
    const vh = view?.innerHeight ?? 600;
    const w = root.offsetWidth || spec.fallbackWidth;
    const h = root.offsetHeight || spec.fallbackHeight;
    const left = parseFloat(root.style.left) || 0;
    const top = parseFloat(root.style.top) || 0;
    root.style.left = `${Math.min(Math.max(0, left), Math.max(0, vw - w))}px`;
    root.style.top = `${Math.min(Math.max(0, top), Math.max(0, vh - h))}px`;
  };

  const open = (): void => {
    placeIfUnplaced();
    clamp();
    // Raise above later-appended same-z siblings by moving to the end of the
    // parent (a re-append does not reset the inline left/top placement).
    if (spec.raiseOnOpen && root.parentNode) root.parentNode.appendChild(root);
    root.hidden = false;
    opened = true;
    spec.onOpen?.();
  };

  const close = (): void => {
    opened = false;
    root.hidden = true;
    spec.onClose?.();
  };

  // Drag by the title bar. Delta-based (start pointer + start left/top) so it needs
  // no getBoundingClientRect, and the move/up listeners live on the document so a
  // drag continues even when the pointer outruns the bar.
  bar.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const startLeft = parseFloat(root.style.left) || 0;
    const startTop = parseFloat(root.style.top) || 0;
    const onMove = (ev: PointerEvent): void => {
      root.style.left = `${startLeft + (ev.clientX - startX)}px`;
      root.style.top = `${startTop + (ev.clientY - startY)}px`;
      clamp();
    };
    const onUp = (): void => {
      doc.removeEventListener('pointermove', onMove);
      doc.removeEventListener('pointerup', onUp);
    };
    doc.addEventListener('pointermove', onMove);
    doc.addEventListener('pointerup', onUp);
  });

  // Right-click anywhere on the window closes it (the UO gump gesture); suppress
  // the browser context menu so the click only closes.
  root.addEventListener('contextmenu', (e) => {
    e.preventDefault();
    close();
  });
  // A small ✕ in the bar is the discoverable, left-click/keyboard equivalent.
  closeBtn?.addEventListener('click', close);
  // Escape closes it while open (only then, so it never swallows a stray Escape).
  if (spec.escapeCloses) {
    doc.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && opened) close();
    });
  }

  return { root, open, close, isOpen: () => opened };
}
